import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useShift } from '@/hooks/useShift';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Pill, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function SporadicMedicationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: patientId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { activeShift } = useShift();

  const [isLoading, setIsLoading] = useState(false);
  const [sporadicTasks, setSporadicTasks] = useState<any[]>([]);
  const [selectedTask, setSelectedTask] = useState<string | null>(null);
  const [medicationName, setMedicationName] = useState('');
  const [dose, setDose] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchSporadicTasks();
  }, [patientId]);

  const fetchSporadicTasks = async () => {
    if (!patientId) return;

    const { data } = await supabase
      .from('care_plan_tasks')
      .select('*')
      .eq('patient_id', patientId)
      .eq('is_sporadic', true)
      .eq('is_active', true);

    setSporadicTasks(data || []);
  };

  const handleSubmit = async () => {
    if (!user || !patientId) return;

    if (!selectedTask && !medicationName.trim()) {
      toast.error(t('sporadicMedication.selectOrName'));
      return;
    }

    setIsLoading(true);

    try {
      // Determine task reference and adhoc fields
      const selectedTaskData = sporadicTasks.find(t => t.id === selectedTask);
      const adhocMedName = selectedTaskData?.name || medicationName.trim();
      const adhocDose = selectedTaskData?.dose || dose.trim() || null;

      // Create task execution with ad-hoc approach (task_id null for new medications)
      const { error: execError } = await supabase
        .from('task_executions')
        .insert({
          task_id: selectedTask || null, // Use existing task or null for ad-hoc
          patient_id: patientId,
          executed_by: user.id,
          shift_id: activeShift?.id || null,
          is_sporadic: true,
          // Ad-hoc fields for when task_id is null
          adhoc_task_type: selectedTask ? null : 'sporadic_medication',
          adhoc_name: selectedTask ? null : `${adhocMedName}${adhocDose ? ` - ${adhocDose}` : ''}`,
          notes: notes.trim() || null
        });

      if (execError) throw execError;

      if (execError) throw execError;

      toast.success(t('sporadicMedication.success'));
      navigate(`/app/caregiver/${patientId}`, { replace: true });
    } catch (error: any) {
      console.error('Error:', error);
      toast.error(error.message || t('sporadicMedication.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const selectedTaskData = sporadicTasks.find(t => t.id === selectedTask);

  return (
    <AppLayout
      title={t('sporadicMedication.title')}
      showBack
      onBack={() => navigate(`/app/caregiver/${patientId}`)}
    >
      <div className="p-4 space-y-4">
        {/* Existing sporadic medications */}
        {sporadicTasks.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t('sporadicMedication.registered')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {sporadicTasks.map((task) => (
                <div
                  key={task.id}
                  onClick={() => {
                    setSelectedTask(task.id === selectedTask ? null : task.id);
                    if (task.id !== selectedTask) {
                      setMedicationName('');
                      setDose('');
                    }
                  }}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${selectedTask === task.id
                    ? 'bg-sporadic/20 border border-sporadic'
                    : 'bg-muted/50 hover:bg-muted'
                    }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedTask === task.id ? 'bg-sporadic text-sporadic-foreground' : 'bg-sporadic/10 text-sporadic'
                    }`}>
                    {selectedTask === task.id ? <Check className="w-5 h-5" /> : <Pill className="w-5 h-5" />}
                  </div>
                  <div>
                    <p className="font-medium">{task.name}</p>
                    {task.dose && (
                      <p className="text-xs text-muted-foreground">{task.dose}</p>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* New medication form */}
        {!selectedTask && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                {sporadicTasks.length > 0 ? t('sporadicMedication.orNew') : t('sporadicMedication.registerNew')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">{t('sporadicMedication.name')}</Label>
                <Input
                  id="name"
                  value={medicationName}
                  onChange={(e) => setMedicationName(e.target.value)}
                  placeholder={t('sporadicMedication.namePlaceholder')}
                />
              </div>
              <div>
                <Label htmlFor="dose">{t('sporadicMedication.dose')}</Label>
                <Input
                  id="dose"
                  value={dose}
                  onChange={(e) => setDose(e.target.value)}
                  placeholder={t('sporadicMedication.dosePlaceholder')}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('sporadicMedication.notes')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('sporadicMedication.notesPlaceholder')}
              rows={3}
            />
          </CardContent>
        </Card>

        {/* Submit */}
        <Button
          className="w-full"
          size="lg"
          disabled={isLoading || (!selectedTask && !medicationName.trim())}
          onClick={handleSubmit}
        >
          {isLoading ? t('sporadicMedication.registering') : t('sporadicMedication.register')}
        </Button>
      </div>
    </AppLayout >
  );
}
